// api/staff-client.ts — staff-side fetch wrapper with separate token storage.
//
// W6 v1 Session 1.
//
// The staff JWT lives under a DIFFERENT localStorage key than the student
// JWT. This is deliberate: mixing the two surfaces' auth tokens risks role
// confusion bugs (e.g. a staff log-out accidentally orphaning a student
// session, or a student token landing in the staff Authorization header).
// Two separate keys, two separate flows, no overlap.

const STAFF_LS_TOKEN_KEY = 'asc_staff_auth_v1';

export function getStaffToken(): string | null {
  return localStorage.getItem(STAFF_LS_TOKEN_KEY);
}

export function setStaffToken(token: string | null): void {
  if (token === null) localStorage.removeItem(STAFF_LS_TOKEN_KEY);
  else localStorage.setItem(STAFF_LS_TOKEN_KEY, token);
}

export class StaffApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

const DEFAULT_API_BASE = 'https://tutor-gateway-staging.autoscuola-ai.workers.dev';

export async function staffApiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const base = import.meta.env.VITE_API_BASE || DEFAULT_API_BASE;
  const token = getStaffToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json');
  const res = await fetch(`${base}${path}`, { ...init, headers });
  return res;
}

export async function staffApiJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await staffApiFetch(path, init);
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* ignore */ }
    throw new StaffApiError(res.status, body, `Staff API ${res.status} at ${path}`);
  }
  return res.json() as Promise<T>;
}

/**
 * Multipart upload helper for the W7 corpus upload widget. We avoid the
 * default JSON Content-Type that `staffApiFetch` auto-sets so the browser
 * can populate the multipart boundary header itself.
 */
export async function staffApiUpload<T>(path: string, body: FormData): Promise<T> {
  const base = import.meta.env.VITE_API_BASE || DEFAULT_API_BASE;
  const token = getStaffToken();
  const headers = new Headers();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  // Intentionally NOT setting Content-Type — fetch + FormData generates it
  // with the multipart boundary. Setting it manually breaks parsing.
  const res = await fetch(`${base}${path}`, { method: 'POST', headers, body });
  if (!res.ok) {
    let parsed: unknown = null;
    try { parsed = await res.json(); } catch { /* ignore */ }
    throw new StaffApiError(res.status, parsed, `Staff API ${res.status} at ${path}`);
  }
  return res.json() as Promise<T>;
}
